import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Trash2, ZoomIn } from "lucide-react";

export function PartCard({ 
  door, 
  index, 
  doorStyles, 
  stylesByCategory, 
  units, 
  onUpdate, 
  onDelete, 
  onViewImage 
}) {
  const currentStyle = doorStyles[door.s];
  const partTypeLabel = door.type === "drawer_front" ? "Drawer Front" : door.type === "panel" ? "Panel" : "Door";
  const unitLabel = units === "mm" ? "mm" : units === "cm" ? "cm" : "in";

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-testid={"door-item-" + index}>
      {/* Header with name and delete */}
      <div className="flex justify-between items-center mb-3">
        <Input 
          value={door.name || ""} 
          onChange={(e) => onUpdate(door.id, "name", e.target.value)} 
          placeholder={"Part " + (index + 1)}
          className="font-medium text-[#6A7359] border-transparent hover:border-gray-300 focus:border-[#6A7359] bg-transparent h-8 w-40 px-2"
          data-testid={"part-name-" + index}
        />
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => onDelete(door.id)} 
          className="text-red-500 hover:text-red-700 hover:bg-red-50" 
          data-testid={"remove-door-btn-" + index}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Part Type Selector */}
      <div className="mb-3">
        <Label className="text-xs text-gray-500">Part Type</Label>
        <Select value={door.type || "door"} onValueChange={(v) => onUpdate(door.id, "type", v)}>
          <SelectTrigger className="border-gray-200 mt-1" data-testid={"part-type-" + index}>
            <SelectValue>{partTypeLabel}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="door">Door</SelectItem>
            <SelectItem value="drawer_front">Drawer Front</SelectItem>
            <SelectItem value="panel">Panel</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Dimensions */}
      <div className="grid grid-cols-[1fr,auto,1fr,1fr] gap-2 items-end">
        <div>
          <Label className="text-xs text-gray-500">Width ({unitLabel})</Label>
          <Input 
            type="text" 
            placeholder="e.g. 12 1/2" 
            value={door.w} 
            onChange={(e) => onUpdate(door.id, "w", e.target.value)} 
            className="border-gray-200" 
            data-testid={"door-width-" + index} 
          />
        </div>
        <button
          type="button"
          onClick={() => {
            const tempW = door.w;
            onUpdate(door.id, "w", door.h);
            onUpdate(door.id, "h", tempW);
          }}
          className="mb-1 p-2 text-[#6A7359] hover:bg-gray-100 rounded transition-colors"
          title="Swap width and height"
          data-testid={"swap-dimensions-" + index}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 16V4M7 4L3 8M7 4L11 8M17 8V20M17 20L21 16M17 20L13 16"/>
          </svg>
        </button>
        <div>
          <Label className="text-xs text-gray-500">Height ({unitLabel})</Label>
          <Input 
            type="text" 
            placeholder="e.g. 30 3/4" 
            value={door.h} 
            onChange={(e) => onUpdate(door.id, "h", e.target.value)} 
            className="border-gray-200" 
            data-testid={"door-height-" + index} 
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Qty</Label>
          <Input 
            type="number" 
            min="1" 
            value={door.q} 
            onChange={(e) => onUpdate(door.id, "q", e.target.value)} 
            className="border-gray-200" 
            data-testid={"door-quantity-" + index} 
          />
        </div>
      </div>

      {/* Style Selector */}
      <div className="mt-3">
        <Label className="text-xs text-gray-500">Style</Label>
        <Select value={door.s} onValueChange={(v) => onUpdate(door.id, "s", v)}>
          <SelectTrigger className="border-gray-200 mt-1" data-testid={"door-style-" + index}>
            <div className="flex items-center gap-2 overflow-hidden">
              {currentStyle && currentStyle.image && (
                <img 
                  src={currentStyle.image} 
                  alt="" 
                  className="w-8 h-8 object-contain rounded border border-gray-200 shrink-0 bg-white" 
                />
              )}
              <span className="truncate">{currentStyle ? currentStyle.name : "Select style"}</span>
            </div>
          </SelectTrigger>
          <SelectContent className="max-h-96">
            {Object.keys(stylesByCategory).map((cat) => (
              <div key={cat}>
                <div className="px-2 py-1.5 text-xs font-bold text-[#6A7359] bg-gray-100 sticky top-0">
                  {cat}
                </div>
                {stylesByCategory[cat].map((s) => (
                  <SelectItem key={s.key} value={s.key} className="py-2">
                    <div className="flex items-center gap-3">
                      <img 
                        src={s.image} 
                        alt={s.name} 
                        className="w-12 h-12 object-contain rounded border border-gray-200 bg-white" 
                        onError={(e) => { e.target.style.display = 'none'; }} 
                      />
                      <div>
                        <div className="font-medium">{s.name}</div>
                        <div className="text-xs text-gray-500">${s.price_per_sqft}/sqft</div>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Grain Direction - Only for Wood Veneer */}
      {currentStyle && currentStyle.category === "Wood Veneer" && (
        <div className="mt-3">
          <Label className="text-xs text-gray-500">Grain Direction</Label>
          <Select value={door.grain || "vertical"} onValueChange={(v) => onUpdate(door.id, "grain", v)}>
            <SelectTrigger className="border-gray-200 mt-1" data-testid={"grain-direction-" + index}>
              <SelectValue>{door.grain === "horizontal" ? "Horizontal" : "Vertical"}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="vertical">Vertical Grain</SelectItem>
              <SelectItem value="horizontal">Horizontal Grain</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* Style Preview */}
      {currentStyle && currentStyle.image && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex gap-4">
            <div className="shrink-0">
              <img 
                src={currentStyle.image} 
                alt={currentStyle.name} 
                className="w-24 h-32 object-contain rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity bg-white p-1" 
                onClick={() => onViewImage(door.s)} 
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#6A7359]">{currentStyle.name}</p>
              <p className="text-xs text-gray-500 mb-2">{currentStyle.category} - ${currentStyle.price_per_sqft}/sqft</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="border-[#6A7359] text-[#6A7359] h-8" 
                onClick={() => onViewImage(door.s)} 
                data-testid={"view-style-" + index}
              >
                <ZoomIn className="w-3 h-3 mr-1" />
                View Full Image
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Options: Hinges and Priming */}
      <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t border-gray-200">
        {/* Hinges - only for doors */}
        {(door.type === "door" || !door.type) && (
          <div className="flex items-center gap-1">
            <Label className="text-xs text-gray-500">Hinges:</Label>
            <Input 
              type="number" 
              min="0" 
              className="w-14 h-7 border-gray-200" 
              value={door.hi} 
              onChange={(e) => onUpdate(door.id, "hi", e.target.value)} 
              data-testid={"door-hinges-" + index} 
            />
            <span className="text-xs text-gray-400">× $4</span>
          </div>
        )}
        {/* Priming - hide for wood veneer */}
        {(!currentStyle || currentStyle.category !== "Wood Veneer") && (
          <div className="flex items-center gap-2">
            <Checkbox 
              checked={door.pr} 
              onCheckedChange={(c) => onUpdate(door.id, "pr", c)} 
              id={"primed-" + door.id} 
              data-testid={"door-primed-" + index} 
            />
            <Label htmlFor={"primed-" + door.id} className="text-sm cursor-pointer">
              Factory Primed (+$15/sqft)
            </Label>
          </div>
        )}
      </div>
    </div>
  );
}
